/**
 * Lists RFC 6901 JSON Pointer paths for a JSON value (for binding pickers).
 */
export function listJsonPointerPaths(
  value: unknown,
  options?: { maxDepth?: number; maxPaths?: number }
): string[] {
  const maxDepth = options?.maxDepth ?? 6;
  const maxPaths = options?.maxPaths ?? 200;
  const paths: string[] = [];

  const visit = (current: unknown, pointer: string, depth: number): void => {
    if (paths.length >= maxPaths) {
      return;
    }
    if (pointer !== '') {
      paths.push(pointer);
    }
    if (depth >= maxDepth) {
      return;
    }
    if (current === null || typeof current !== 'object') {
      return;
    }
    if (Array.isArray(current)) {
      const limit = Math.min(current.length, 20);
      for (let index = 0; index < limit; index++) {
        const segment = String(index);
        visit(
          current[index],
          pointer === '' ? `/${segment}` : `${pointer}/${segment}`,
          depth + 1
        );
      }
      return;
    }
    for (const [key, child] of Object.entries(current)) {
      const token = encodeJsonPointerToken(key);
      visit(
        child,
        pointer === '' ? `/${token}` : `${pointer}/${token}`,
        depth + 1
      );
    }
  };

  visit(value, '', 0);
  return paths.sort();
}

function encodeJsonPointerToken(token: string): string {
  return token.replace(/~/g, '~0').replace(/\//g, '~1');
}

export function readParamBindings(
  data: Record<string, unknown> | null
): Record<string, string> {
  if (data === null) {
    return {};
  }
  const raw = data['paramBindings'];
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  const bindings: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === 'string' && value !== '') {
      bindings[key] = value;
    }
  }
  return bindings;
}
